import React, { useEffect, useState } from 'react';
import { Box, Center, Image, Spinner, Text } from '@chakra-ui/react';
import { FFLDealerMap } from '@masterffl/ffl-map-component';
import { renderToString } from 'react-dom/server';
import { formatPhoneNumber } from '../ffl-detail/utils.mjs';
import { useClient } from '@/api/backoffice-api';
import AppConfig from '@/utils/app-config.mjs';
import {
  getLicenseByState,
  getLicenseByZipDistance,
  getLicenseByZipQueryParams
} from '../ducks/selectors';
import { PhoneNumberRecognizer, Recognizer, ZipCodeRecognizer } from '@masterffl/ffl-search-v2-component';
import FFLDetailModal from '../ffl-detail/FFLDetailModal';
import { useNavigate } from 'react-router';
import { useLocation } from 'react-router-dom';

interface Props {
  query: string;
  cognitoGroup: boolean;
  isSuperAdmin: boolean;
  searchRecognizer: Recognizer;
  containerHeight: string;
  isReAssignDealer?: boolean;
  onDealerAssigned?(arg: any): void;
}

const FFLMapView = ({
  query,
  cognitoGroup,
  isSuperAdmin,
  searchRecognizer,
  containerHeight,
  isReAssignDealer,
  onDealerAssigned
}: Props) => {
  const [mapPageNumber, setMapPageNumber] = useState(1);
  const [dealersForMap, setDealersForMap] = useState<any[]>([]);
  const [licenseId, setLicenseId] = useState('');
  const getLicenseByZipParams = getLicenseByZipQueryParams();
  const getDistance = getLicenseByZipDistance();
  const licenseByState = getLicenseByState();
  const [pageSize, setPageSize] = useState(200);
  const [searchString, setSearchString] = useState(query);
  const isShowOnlyNFAFilterDisabled = false; // Set this if needed as prop

  // Param cleanup logic (copied from DealerCardResults-v2)
  const params: any = { ...getLicenseByZipParams };
  if (params?.delivery_indicator && params?.delivery_indicator_commercial) {
    delete params.delivery_indicator;
    delete params.delivery_indicator_commercial;
  }
  if (params?.delivery_indicator || params?.delivery_indicator_commercial) {
    params.delivery_indicator = params?.delivery_indicator ? params?.delivery_indicator : params?.delivery_indicator_commercial;
    delete params?.delivery_indicator_commercial;
  }
  if (params?.state) {
    delete params.state;
  }

  // Recognizer-based param logic
  const getApiParams = React.useMemo(() => {
    let apiParams = {};
    if (searchRecognizer instanceof ZipCodeRecognizer) apiParams = { zipCode: query };
    else if (searchRecognizer.constructor?.name === 'FFLRecognizer') apiParams = { license_key: query };
    else if (searchRecognizer.constructor?.name === 'ControlNumberRecognizer') apiParams = { control_number: query };
    else if (searchRecognizer.constructor?.name === 'EINNumberRecognizer') apiParams = { ein_number: query };
    else if (searchRecognizer instanceof PhoneNumberRecognizer) apiParams = { phone_number: query };
    else apiParams = { business_name: query };
    return apiParams;
  }, [searchRecognizer, query]);

  const navigate = useNavigate();
  const location = useLocation();
  const urlSearchParams = new URLSearchParams(location.search);
  const urlPageSize = Number(urlSearchParams.get('page_size'));

  const licenseStates = licenseByState && licenseByState.length > 0 && licenseByState.length !== 55 ? licenseByState?.join(',') : '';
  console.log({getLicenseByZipParams})
  const [{ data: mapData, loading: mapDataLoading, error: mapDataError }] =
    useClient({
      baseURL: AppConfig.fflExpressServiceApiURL,
      url: `/dealers/search`,
      timeout: 60000,
      params: {
        ...params,
        types: params?.types ? params?.types : ['01','02','03','06','07','08','09','10','11'].toString(),
        ...{ distance: getDistance, ...getApiParams, states: licenseStates },
        ...(isShowOnlyNFAFilterDisabled ? { exclude_non_sot_dealer: true } : {}),
        page_size: urlPageSize ? urlPageSize : pageSize,
        page_number: mapPageNumber
      }
    });

  useEffect(() => {
    if (mapData && mapData.metadata && mapData.metadata.hasMorePages && !mapDataLoading) {
      setMapPageNumber(mapPageNumber + 1);
    }
    if (
      mapData &&
      mapData.data &&
      mapData.data.length > 0 &&
      !mapDataLoading
    ) {
      const mapComponentData = mapData.data
        .filter((dealer: any) => 
          dealer.geoLocation && 
          dealer.geoLocation?.lat != null && 
          dealer.geoLocation?.lon != null
        )
        .map((dealer: any) => {
          return { ...dealer, geo: { lat: dealer.geoLocation.lat, lng: dealer.geoLocation.lon } }
        });
      setDealersForMap((prevDealers) => [...prevDealers, ...mapComponentData]);
    }
  }, [mapData, mapDataLoading]);

  useEffect(() => {
    setDealersForMap([]);
    setMapPageNumber(1);
  }, [getDistance, JSON.stringify(params), query, mapDataError, licenseByState]);

  const getAddress = (address: any) => {
    return `${address?.street1}, ${address?.city}, ${address?.state}, ${address?.zip}`;
  };

  const getLinks = (dealer: any) => {
    return (
      <>
        {dealer && dealer.phoneNumbers && dealer.phoneNumbers.length > 0 && (
          <a className='phone-number tooltip-wrapper-custom'>
            <img src="/images/ui/square-phone.svg" width="35" height="35" />
            <Text className="distance-text business-type-ellipsis max-w-80 business-type-tooltip phonenumber"
              sx={{ textTransform: 'capitalize' }}
            >
              {formatPhoneNumber(dealer.phoneNumbers[0] as string)}
            </Text>
          </a>
        )}
        {dealer && dealer.website && (
          <a
            href={`${dealer.website}`} target='blank' className='globe_icon'
          >
            <img src="/images/ui/www.svg" width="35" height="35"/>
          </a>
        )}
        {dealer?.socialProfiles?.google && (
          <a href={dealer.socialProfiles.google} target="blank">
            <img src="/images/ui/square-google.svg" width="35" height="35" />
          </a>
        )}
        {dealer?.socialProfiles?.facebook && (
          <a href={dealer.socialProfiles.facebook} target="blank">
            <img src="/images/ui/square-fb.svg" width="35" height="35" />
          </a>
        )}
        {dealer?.socialProfiles?.yelp && (
          <a href={dealer.socialProfiles.yelp} target="blank">
            <img src="/images/ui/square-yelp.svg" width="35" height="35" />
          </a>
        )}
      </>
    );
  };

  const getLicenseTypesArray = (dealer: any) => {
    const sotType = {
      type: 'SOT',
      hasValidCertificate: true,
      viewType: 'SOT',
    };
    if (dealer) {
      if (dealer.licenseTypes && dealer.licenseTypes.length > 0) {
        if (dealer?.isSotDealer) {
          return [sotType, ...dealer.licenseTypes.slice(0, 2)];
        } else {
          return [...dealer.licenseTypes];
        }
      } else if (dealer?.isSotDealer) {
        return [sotType];
      } else {
        return [];
      }
    }
  }

  const getLicenseTypeElement = (dealer: any) => {
    const licenseTypes = getLicenseTypesArray(dealer);
    if (licenseTypes && licenseTypes.length > 0) {
      const licenseTypeHTML = (
        <>
          {licenseTypes.map((licenseType: any, index: number) => (
            <div
              className={`info-type-box ${licenseType?.hasValidCertificate ? 'info-type-box-active' : ''
                }`}
              key={`info-type-box-${index}`}
            >
              <span
                className={`info-type-text ${licenseType?.hasValidCertificate ? 'info-type-text-active' : ''
                  }`}
              >
                {licenseType.type}
              </span>
            </div>
          ))}
        </>
      );
      return licenseTypeHTML;
    }
    return <></>;
  };

  const createDealerCard = (dealer: any) => {
    const links = renderToString(getLinks(dealer));
    const licenseTypesList = renderToString(getLicenseTypeElement(dealer));
    return `<div class="info-container">
    <div class="info-title-row">
      <p class="name-label">${dealer?.primaryBusinessName ?? ''}</p>
      <div class="info-b-type-row">
        ${licenseTypesList}
      </div>
    </div>
    <div class="info-title-row mobile-start">
      <p class="address-label">${dealer?.address?.street ?? ''}, ${dealer?.address?.city ?? ''}, ${dealer?.address?.state ?? ''} ${dealer?.address?.zipCode ?? ''}</p>
      <div class="info-b-type-row w-40">
      <div class="review-row tooltip-wrapper-custom">
            ${dealer?.primaryBusinessType
                ? `<img src="/images/ui/briefcase.svg" class="BriefcaseIconFont" /> <p class="decs-text b-type-ellipsis business-type-tooltip">${dealer?.primaryBusinessType}</p>`
                : ''
            }
            </div>
            ${dealer?.isCommercial !== null ? `
              <div class="info-container__icons tooltip-wrapper-custom">
                  ${dealer?.isCommercial === true ? `
                      <img src="/images/ui/commercial.svg" /> ` : `<img src="/images/ui/residential.svg" />`}
                      <p
                        class="distance-text business-type-ellipsis business-type-tooltip"
                    >
                        ${dealer?.isCommercial ? 'Commercial' : 'Residential'}
                    </p>
              </div>
            `:''}
      </div>
    </div>
    <div class="review-row" style="justify-content: space-between;">
    <div class="review-row w-50" style="margin-top: 8px;">
      <img src="/images/ui/star.svg" />
      <p class="decs-text mt-1">${dealer?.consolidatedRatings?.ratings ?? 'No ratings'
      }</p>
      ${dealer?.consolidatedRatings?.reviews
        ? `<span class="gray-text">(${dealer.consolidatedRatings.reviews} reviews)</span>`
        : ''
      }
    </div>
     ${dealer?.distance
        ? `<div class="review-row flex-end max-w-50 tooltip-wrapper-custom" style="margin-top: 8px">
              <img src="/images/ui/location.svg" />
              <p class="decs-text miles-text mr-0">${dealer?.distance.toFixed(1)} miles</p>
              <p class="distance-text business-type-tooltip">
                ${dealer?.distance.toFixed(1)} miles from center
              </p>
            </div>`
        : ''
      }
      </div>
    <div class="info-title-row mb-8">
     ${dealer && dealer.firearms && (dealer.firearms.handGun || dealer.firearms.longGun || dealer.firearms.other || dealer.firearms.nfa) ? `
      <div class="info-fees-wrapper mobile-wrap">
       ${dealer && dealer.firearms && dealer.firearms.handGun ? `
        <div class="info-fees-row">
          <img src="/images/ui/gun.svg" />
          <p class="decs-text">$${dealer.firearms.handGun}</p>
        </div>
        `: ''}
        ${dealer && dealer.firearms && dealer.firearms.longGun ? `
        <div class="info-fees-row">
          <img src="/images/ui/long-gun.svg" />
          <p class="decs-text"> $${dealer.firearms.longGun}</p>
        </div>
        `: ''}
        ${dealer && dealer.firearms && dealer.firearms.other ? `
        <div class="info-fees-row">
          <img src="/images/ui/gun-tools.svg" />
          <p class="decs-text">$${dealer.firearms.other}</p>
        </div>
        `: ''}
        ${dealer && dealer.isSotDealer && dealer.firearms && dealer.firearms.nfa ? `
        <div class="info-fees-row">
          <p class="decs-text">NFA</p>
          <p class="decs-text">
             $${dealer.firearms.nfa}</p>
        </div>
        ` : ''}
      </div>
      ` : ''}
      ${dealer?.isRangeDealer ?  `
      <div class="info-range-icon tooltip-wrapper-custom">
        <img src="/images/ui/shooting-range.svg" title="Shooting range" /> 
        <p class="distance-text business-type-tooltip">Shooting range</p>
      </div>
      `: ''}
    </div>
    <div class="review-row mt-2 align-end">
      <div class="social-links">
        ${links}
      </div>
      <div class="action-col">
        <button id="info-view-btn" class="map__primary-btn" data-value="${dealer?.dealerId ?? ''
      }">${isReAssignDealer && onDealerAssigned ? 'Select' : 'View Details'}</button>
      </div>
    </div>
  </div>`;
  };

  const onNavigateDetails = (id: string) => {
    window.open(`/dealers-search/${id}`, '_blank');
  };

  const onDetailsModelClose = () => {
    setLicenseId('');
  };

  const noDataComponent = () => {
    return (
      <Box className="not-found-box d-no-data">
        <Image src="/images/ui/not-found-icon.png" alt="no data" />
        <>
          <p className="not-found-box__title">No FFL Dealers found</p>
          <p className="not-found-box__message">
            Try adjusting your search or filter to find what you&apos;re looking for
          </p>
        </>
      </Box>
    );
  };

  return (
    <>
      {dealersForMap && dealersForMap.length > 0 ? (
        <Box className="map-container">
          <FFLDealerMap
            mapKey={AppConfig.mapAPIKey}
            dealerList={dealersForMap}
            getDealerInfoCardHtml={createDealerCard}
            cardBtnClickHandler={onNavigateDetails}
            height={containerHeight ? containerHeight : 'calc(100vh - 253px)'}
          />
        </Box>
      ) : !dealersForMap.length && mapDataLoading && !mapDataError ? (
        <Center height={'calc(100vh - 253px)'}>
          <Spinner />
        </Center>
      ) : (
        !dealersForMap.length && !mapDataLoading && noDataComponent()
      )}
      <FFLDetailModal
        id={licenseId}
        isOpen={licenseId ? true : false}
        onClose={onDetailsModelClose}
        cognitoGroup={cognitoGroup}
        isSuperAdmin={isSuperAdmin}
      />
    </>
  );
};

export default FFLMapView;
